import React, { useRef, useState, useCallback, useEffect } from 'react';
import { EventParameter } from '../../../generated/api/models/EventParameter';
import ContentEditable from '../ContentEditable';
import nunjucks from 'nunjucks';
import ParameterChips from './ParameterChips';
import AITemplateAssistant from '../AITemplateAssistant';
import { convertValueByType, getDefaultValue } from './typeUtils';
import './style.scss';

interface ParameterEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  parameters?: EventParameter[];
  showPreview?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

const ParameterEditor: React.FC<ParameterEditorProps> = ({
  value = '',
  onChange,
  placeholder = 'Enter your message...',
  parameters = [],
  showPreview = true,
  className = '',
  style = {},
}) => {
  // State for custom parameter values (for interactive preview)
  const [customValues, setCustomValues] = useState<{ [paramName: string]: any }>({});

  // Generate example values for preview with type conversion
  const getExampleValue = (param: EventParameter): any => {
    // First check if user has customized this value
    if (param.name in customValues) {
      return customValues[param.name];
    }

    // Otherwise use default example and convert by type to match backend
    return getDefaultValue(param.example, param.type);
  };

  // Handle custom value changes from ParameterChips
  const handleValueChange = useCallback((paramName: string, value: any) => {
    setCustomValues((prev) => ({
      ...prev,
      [paramName]: value,
    }));
  }, []);

  // Handle resetting a parameter to its default value
  const handleResetValue = useCallback((paramName: string) => {
    setCustomValues((prev) => {
      const newValues = { ...prev };
      delete newValues[paramName];
      return newValues;
    });
  }, []);

  // Convert template string to preview with example values using Nunjucks
  const getPreviewText = (text: string): string => {
    try {
      // Create nested context object with parameter values
      // e.g., "patient.email" becomes { patient: { email: "value" } }
      const context = parameters.reduce(
        (acc, param) => {
          const parts = param.name.split('.');
          let current: any = acc;

          // Build nested object structure
          for (let i = 0; i < parts.length - 1; i++) {
            const part = parts[i];
            if (part) {
              if (!current[part]) {
                current[part] = {};
              }
              current = current[part];
            }
          }

          // Set the final value
          //@ts-expect-error
          current[parts[parts.length - 1]] = getExampleValue(param);
          return acc;
        },
        {} as { [key: string]: any },
      );

      // Render template with Nunjucks
      return nunjucks.renderString(text, context);
    } catch (error) {
      console.error('Error rendering template:', error);
      return text;
    }
  };

  const editorNameRef = useRef(`parameter-editor-${Math.random().toString(36).substr(2, 9)}`);
  const [savedSelection, setSavedSelection] = useState<{ start: number; end: number } | null>(null);

  // Save current cursor position using a simpler approach
  const saveCursorPosition = useCallback(() => {
    const editorElement = document.getElementById(editorNameRef.current);
    if (!editorElement) return;

    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      if (
        editorElement.contains(range.commonAncestorContainer) ||
        range.commonAncestorContainer === editorElement
      ) {
        // Get the current text content
        const fullText = editorElement.innerText || '';

        // Create a temporary range to measure cursor position
        const tempRange = document.createRange();
        tempRange.setStart(editorElement.firstChild || editorElement, 0);
        tempRange.setEnd(range.startContainer, range.startOffset);

        // Get text content up to cursor position
        const beforeCursor = tempRange.toString();
        const start = beforeCursor.length;

        // For end position, extend to range end
        tempRange.setEnd(range.endContainer, range.endOffset);
        const beforeAndSelected = tempRange.toString();
        const end = beforeAndSelected.length;

        setSavedSelection({ start, end });
      }
    }
  }, []);

  // Restore cursor position using a simpler text-based approach
  const restoreCursorPosition = useCallback(
    (textOffset: number = 0) => {
      const editorElement = document.getElementById(editorNameRef.current);
      if (!editorElement || !savedSelection) return;

      // Calculate new cursor position
      const newPosition = savedSelection.start + textOffset;

      try {
        // Get all text nodes and calculate position
        const walker = document.createTreeWalker(editorElement, NodeFilter.SHOW_TEXT);

        let currentPos = 0;
        let targetNode: Node | null = null;
        let targetOffset = 0;

        while (walker.nextNode()) {
          const node = walker.currentNode;
          const nodeText = node.textContent || '';
          const nodeLength = nodeText.length;

          if (currentPos + nodeLength >= newPosition) {
            targetNode = node;
            targetOffset = newPosition - currentPos;
            break;
          }

          currentPos += nodeLength;
        }

        if (targetNode) {
          const selection = window.getSelection();
          const range = document.createRange();

          // Ensure offset doesn't exceed node length
          const safeOffset = Math.min(targetOffset, targetNode.textContent?.length || 0);

          range.setStart(targetNode, safeOffset);
          range.setEnd(targetNode, safeOffset);
          selection?.removeAllRanges();
          selection?.addRange(range);
        } else {
          // Fallback: position at end of content
          const selection = window.getSelection();
          const range = document.createRange();
          range.selectNodeContents(editorElement);
          range.collapse(false);
          selection?.removeAllRanges();
          selection?.addRange(range);
        }
      } catch (e) {
        console.warn('Could not restore cursor position:', e);
        // Fallback: just focus the editor
        editorElement.focus();
      }

      // Clear saved selection
      setSavedSelection(null);
    },
    [savedSelection],
  );

  // Handle inserting a parameter at the saved cursor position
  const insertParameter = (paramName: string) => {
    const paramText = `{{${paramName}}}`;
    const editorElement = document.getElementById(editorNameRef.current);

    if (!editorElement) {
      // Fallback: append to end of text if editor not found
      const newValue = value + paramText;
      onChange(newValue);
      return;
    }

    // If we have a saved cursor position, use it
    if (savedSelection) {
      // Use the actual text content from the editor to ensure consistency
      const currentText = editorElement.innerText || '';
      const beforeCursor = currentText.substring(0, savedSelection.start);
      const afterCursor = currentText.substring(savedSelection.end);
      const newValue = beforeCursor + paramText + afterCursor;

      onChange(newValue);

      // Focus editor and restore cursor position after the inserted text
      setTimeout(() => {
        editorElement.focus();
        restoreCursorPosition(paramText.length);
      }, 0);

      return;
    }

    // Check if the editor is currently focused and has a selection
    const selection = window.getSelection();
    const isEditorFocused =
      document.activeElement === editorElement || editorElement.contains(document.activeElement);

    if (isEditorFocused && selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);

      // Make sure the selection is within the editor
      if (
        editorElement.contains(range.commonAncestorContainer) ||
        range.commonAncestorContainer === editorElement
      ) {
        // Insert the parameter text at cursor position
        const textNode = document.createTextNode(paramText);
        range.deleteContents();
        range.insertNode(textNode);

        // Move cursor after inserted text
        range.setStartAfter(textNode);
        range.setEndAfter(textNode);
        selection.removeAllRanges();
        selection.addRange(range);

        // Trigger the ContentEditable onChange by dispatching an input event
        const inputEvent = new Event('input', { bubbles: true });
        editorElement.dispatchEvent(inputEvent);

        return;
      }
    }

    // Fallback: append to end of text and focus editor
    const newValue = value + paramText;
    onChange(newValue);

    // Focus the editor and move cursor to end
    setTimeout(() => {
      editorElement.focus();
      const range = document.createRange();
      const selection = window.getSelection();
      range.selectNodeContents(editorElement);
      range.collapse(false); // Move to end
      selection?.removeAllRanges();
      selection?.addRange(range);
    }, 0);
  };

  // Handle ContentEditable onChange - preserve whitespace during editing
  const handleContentChange = (e: { target: { value: string } }) => {
    onChange(e.target.value);
  };

  // Handle editor blur to save cursor position
  const handleEditorBlur = useCallback(() => {
    saveCursorPosition();
  }, [saveCursorPosition]);

  // Handle key events to save cursor position periodically
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      // Save cursor position on certain key events
      if (
        e.key === 'ArrowLeft' ||
        e.key === 'ArrowRight' ||
        e.key === 'ArrowUp' ||
        e.key === 'ArrowDown'
      ) {
        setTimeout(saveCursorPosition, 0);
      }
    },
    [saveCursorPosition],
  );

  // Attach event listeners to the editor
  useEffect(() => {
    const editorElement = document.getElementById(editorNameRef.current);
    if (!editorElement) return;

    // Add event listeners
    editorElement.addEventListener('blur', handleEditorBlur);
    editorElement.addEventListener('keydown', handleKeyDown);
    editorElement.addEventListener('mouseup', saveCursorPosition); // Save position after mouse clicks

    // Cleanup
    return () => {
      editorElement.removeEventListener('blur', handleEditorBlur);
      editorElement.removeEventListener('keydown', handleKeyDown);
      editorElement.removeEventListener('mouseup', saveCursorPosition);
    };
  }, [handleEditorBlur, handleKeyDown, saveCursorPosition]);

  return (
    <div className={`parameter-editor ${className}`} style={style}>
      {/* AI Assistant */}
      <AITemplateAssistant
        currentTemplate={value}
        parameters={parameters}
        onApply={(template) => onChange(template)}
        outputFormat="plaintext"
      />

      {/* Side-by-side editor and preview */}
      <div className="editor-preview-container">
        {/* Left side: Editor */}
        <div className="editor-section">
          <div className="editor-container">
            <ContentEditable
              text={value || ''}
              onChange={handleContentChange}
              placeholder={placeholder}
              name={editorNameRef.current}
            />
          </div>
        </div>

        {/* Right side: Preview */}
        {showPreview && (
          <div className="preview-section">
            <div className="preview-container">
              <div className="preview-label">Preview:</div>
              <div className="preview-content">
                {value ? (
                  parameters.length > 0 ? (
                    getPreviewText(value)
                  ) : (
                    value
                  )
                ) : (
                  <div className="preview-placeholder">Preview will appear here...</div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <ParameterChips
        parameters={parameters}
        onInsertParameter={insertParameter}
        currentValues={customValues}
        onValueChange={handleValueChange}
        onResetValue={handleResetValue}
      />
    </div>
  );
};

export default ParameterEditor;
